import * as React from "react";
import { I18n } from "i18n-js";
import * as locales from "../../../../locales.json";
const i18n = new I18n(locales);
i18n.defaultLocale = "en";
i18n.locale = (window as any).locale || "";
import styled from "styled-components";

import Modal from "../../../reusable/reactModal";
import { FlexSettingsModalProps } from "../lib/types";
import {
  StyledButton,
  StyledButtonContainer,
  StyledLabel,
  StyledModalBody,
  StyledModalContents,
  StyledModalSubHeader,
  StyledModalText,
  StyledSelect,
} from "../lib/styles";
import { convertCurrencyToIntString } from "../../../../utils";
import { redesignTheme } from "../../../theme/theme";

const infoCircle = require("../../../svg/infoCircle.svg");

const StyledDescription = styled(StyledModalText)`
  margin-bottom: ${redesignTheme.spacing[4.5]};
`;

const StyledInfoText = styled(StyledModalText)`
  font-size: ${redesignTheme.fontSize[50]};
  line-height: 20px;
`;

const StyledHelper = styled(StyledModalText)`
  color: ${redesignTheme.color.gray500};
  font-size: ${redesignTheme.fontSize[50]};
  line-height: 20px;
  margin-bottom: ${redesignTheme.spacing[4.5]};
`;

const StyledInfoImg = styled.img`
  margin-right: ${redesignTheme.spacing[2.5]};
  margin-top: ${redesignTheme.spacing[1.5]};
  height: 19px;
  width: 19px;
`;

const StyledInfoWrapper = styled.div`
  background: ${redesignTheme.color.gray50};
  border-radius: ${redesignTheme.radius.sm};
  padding: ${redesignTheme.spacing[2]} ${redesignTheme.spacing[2.5]};
  display: flex;
  align-items: flex-start;
  flex-direction: row;
`;

const FlexSettingsModal = ({
  amount,
  closeModal,
  isModifying,
  existingAmount,
  modal,
  setAmount,
  setModal,
  userCount,
}: FlexSettingsModalProps): React.ReactElement => {
  const amountHasNotChanged =
    convertCurrencyToIntString(existingAmount) === amount;

  return (
    <Modal
      ariaLabel={isModifying ? "modify flex plan" : "switch to flex plan"}
      close={closeModal}
      showDialog={modal === "flexSettings"}
      testId={"flex-settings-modal"}
      title={
        isModifying
          ? i18n.translate("admin_pages.settings.billing.modify_flex_plan")
          : i18n.translate("admin_pages.settings.billing.switch_to_flex_plan")
      }
    >
      <StyledModalBody>
        <StyledModalContents>
          <StyledModalSubHeader>
            {i18n.translate(
              "admin_pages.settings.billing.choose_amount_header"
            )}
          </StyledModalSubHeader>
          <StyledDescription>
            {i18n.translate(
              "admin_pages.settings.billing.choose_amount_description"
            )}
          </StyledDescription>
          <StyledLabel htmlFor="amount">
            {i18n.translate("admin_pages.settings.billing.choose_amount_label")}
          </StyledLabel>
          <StyledSelect
            data-testid="bill-amount-select"
            id="amount"
            onChange={(event): void => {
              return setAmount(event.target.value);
            }}
            value={amount}
          >
            <option value="">
              {i18n.translate(
                "admin_pages.settings.billing.choose_amount_placeholder"
              )}
            </option>
            <option value="200">$200.00</option>
            <option value="500">$500.00</option>
            <option value="1000">$1,000.00</option>
            <option value="2000">$2,000.00</option>
            <option value="3000">$3,000.00</option>
            <option value="4000">$4,000.00</option>
            <option value="5000">$5,000.00</option>
            <option value="10000">$10,000.00</option>
            <option value="15000">$15,000.00</option>
            <option value="20000">$20,000.00</option>
          </StyledSelect>

          <StyledHelper>
            {i18n.translate(
              "admin_pages.settings.billing.choose_amount_helper"
            )}
          </StyledHelper>

          <StyledInfoWrapper>
            <StyledInfoImg src={infoCircle} alt="info circle" />
            <StyledInfoText>
              {i18n.translate(
                "admin_pages.settings.billing.choose_amount_info",
                { userCount }
              )}
            </StyledInfoText>
          </StyledInfoWrapper>
        </StyledModalContents>
        <StyledButtonContainer>
          <StyledButton
            onClick={closeModal}
            size="small"
            testId="cancel-button"
            variant="secondary"
          >
            {i18n.translate("forms.cancel")}
          </StyledButton>
          <StyledButton
            disabled={!amount || amountHasNotChanged}
            onClick={() =>
              setModal(isModifying ? "flexPayment" : "flexConfirm")
            }
            size="small"
            testId="next-button"
            variant="primary"
          >
            {i18n.translate("forms.next")}
          </StyledButton>
        </StyledButtonContainer>
      </StyledModalBody>
    </Modal>
  );
};

export default FlexSettingsModal;
